<template>
  <div>

    <div class="row">
      <div class="col-sm-7 col-lg-4 text-center">
        <div class="form-inline mb-3">
          <div class="form-group">
            <label class="sr-only" for="search">Search</label>
            <input
              id="search"
              type="search"
              name="search"
              class="form-control"
              placeholder="Search..."
              v-model="search"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="table-responsive">
      <b-table
        class="dataTable"
        striped
        :fields="fields"
        :items="models"
        :busy="isBusy"
        :filter="search"
        @filtered="fillImages"
        @sort-changed="fillImages"
      >
        <div slot="table-busy" class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>

        <template #cell(formatName)="data">
          {{ formatModelFormat(data.item.format) }}
        </template>

        <template #cell(actions)="data">
          <a
            href="javascript:void(0)"
            @click="showNameEditor(data.item.guid, data.item.name)"
            class="d-block d-md-inline"
          >
            <span class="icon icon-edit"></span>
          </a>
          <a
            href="javascript:void(0)"
            @click="showViewer(data.item.guid, data.item.format)"
            class="d-block d-md-inline"
          >
            <span class="icon icon-view"></span>
          </a>
          <a href="javascript:void(0)" @click="removeModel(data.item.guid)" class="d-block d-md-inline">
            <span class="icon icon-trash" />
          </a>
        </template>

        <template #cell(image)="data">
          <b-spinner class="align-middle" v-show="!thumbnails[data.item.guid]" :key="imageLoaderKey"></b-spinner>
          <div class="image-container d-none d-sm-table-cell">
            <img :ref="'thumbnail-' + data.item.guid" class="model-image image-small" src="" alt="" />
          </div>
        </template>
      </b-table>
    </div>

    <!-- Modals -->
    <b-modal
      :id="MODAL_ID_UPLOAD"
      size="lg"
      centered
      role="dialog"
      modal-class="regular"
      hide-header-close
      hide-footer
    >
      <template slot="modal-header" slot-scope="{ close }">
        <h5 class="modal-title">Upload Model</h5>
        <button type="button" class="close" @click="close()" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </template>
      <template slot="default">
        <UploadModel
          :storage-type="storageType"
          :formats="modelFormats"
          v-on:model-uploaded="onModelUploaded"
        />
      </template>
    </b-modal>

    <!-- View Model -->
    <b-modal
      :id="MODAL_ID_VIEW"
      title="Model Viewer"
      size="lg"
      centered
      role="dialog"
      modal-class="regular"
      :ok-only="true"
      ok-title="Close"
    >
      <template slot="default">
        <ViewModel
          :guid="viewedModelGuid"
          :format="viewedModelFormat"
          v-if="showViewerComponent"
        />
      </template>
    </b-modal>

    <!-- Rename Model -->
    <b-modal
      :id="MODAL_ID_RENAME"
      size="lg"
      centered
      role="dialog"
      modal-class="regular"
      hide-header-close
    >
      <template slot="modal-header" slot-scope="{ close }">
        <h5 class="modal-title">Model Rename</h5>
        <button type="button" class="close" @click="close()" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </template>
      <template slot="default">
        <div class="custom-modal-content">
            <b-container>
              <b-row>
                <b-col sm="0" cols="9">
                  <b-container class="label">Model name:<span class="important-label">*</span></b-container>
                  <b-form-input
                      v-model="$v.editedModelName.$model"
                      placeholder="Name"
                      class="modal-input"
                      :state="validationUtils.validateState($v, 'editedModelName')"
                  >
                  </b-form-input>
                  <b-form-invalid-feedback>
                      This is a required field.
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
            </b-container>
        </div>
      </template>
      <template slot="modal-footer">
        <div>
          <b-button type="button" variant="blue" @click="renameModel">Rename</b-button>
        </div>
      </template>
    </b-modal>

  </div>
</template>

<script>
import * as harpraApi from "@/services/api.service";
import * as routerUtils from "@/utils/router-utils";
import * as validationUtils from '@/utils/validation-utils';
import * as formatUtils from '@/utils/format-utils';
import UploadModel from "@/components/Views/UploadModel";
import ViewModel from "@/components/Views/ViewModel";
import { required } from 'vuelidate/lib/validators';

export default {
  name: "ModelsGrid",
  props: {
    storageType: String,
    modelFormats: String,
  },
  components: {
    UploadModel,
    ViewModel,
  },
  data() {
    return {
      MODAL_ID_UPLOAD: "upload-model-modal",
      MODAL_ID_RENAME: "rename-model-modal",
      MODAL_ID_VIEW: "view-model-modal",
      isSubscriptionActive: false,
      isBusy: true,
      fields: [
        { key: 'image', label: '' },
        { key: "name", label: "Name", sortable: true },
        { key: "formatName", label: "Format", sortable: true },
        { key: "fileSize", label: "Size", formatter: "formatSize", sortable: true },
        { key: "creationDate", label: "Date Added", formatter: "formatDate", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      models: [],
      imageLoaderKey: 0,
      thumbnails: [],
      search: "",
      viewedModelGuid: '',
      viewedModelFormat: 'fbx',
      editedModelGuid: '',
      editedModelName: '',
      showViewerComponent: false,
    };
  },
  computed: {
    validationUtils() {
      return validationUtils;
    },
  },
  validations: {
    editedModelName: {
      required,
    }
  },
  methods: {
    formatDate: formatUtils.formatDate,
    formatSize: formatUtils.formatSize,
    formatModelFormat: formatUtils.formatModelFormat,
    async loadModels() {
      this.isBusy = true;
      this.models = await harpraApi.getModels(this.$AuthService.token.accessToken, this.storageType);
      this.$emit("models-loaded", this.models);
      this.fillImages();
      this.isBusy = false;
    },
    removeModel(guid) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to remove this model?', {
        title: 'Remove model',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelVariant: "black",
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
        modalClass: "regular"
      }).then(async value => {
        if (!value) {
          return;
        }

        await harpraApi.deleteModel(guid);
        await this.loadModels();
      });
    },
    showViewer(guid, format) {
      this.$bvModal.show(this.MODAL_ID_VIEW);
      this.viewedModelGuid = guid;
      this.viewedModelFormat = format;
      this.showViewerComponent = true;
    },
    showNameEditor(guid, name) {
      this.$bvModal.show(this.MODAL_ID_RENAME);
      this.editedModelGuid = guid;
      this.editedModelName = name;
      this.$v.$reset();
    },
    renameModel(evt) {
      this.$v.$touch();

      if (this.$v.$anyError) {
        return;
      }

      evt.preventDefault();

      this.$AuthService.getAccessToken(
        async () => {
          await harpraApi.renameModel(
            this.editedModelGuid,
            this.editedModelName,
            this.$AuthService.token.accessToken);

          await this.loadModels();

          this.isBusy = false;
          this.$root.$emit("bv::hide::modal", this.MODAL_ID_RENAME);
          this.editedModelGuid = "";
          this.editedModelName = "";
        },
        () => {
          // eslint-disable-next-line no-console
          console.log("[ERROR] Couldn't get access token in renameModel().");
        });
    },
    fillImages() {
      for (let item of this.models) {
        this.getImage(item.guid);
      }
    },
    getImage(guid) {
      if (this.thumbnails[guid]) {
        this.setImageSource(guid);
        return;
      }

      this.$AuthService.getAccessToken(
        async () => {
          let thumbnail = await harpraApi.getThumbnail(guid, this.$AuthService.token.accessToken);

          this.thumbnails[guid] = thumbnail;
          this.setImageSource(guid);
        },
        () => {
          // eslint-disable-next-line no-console
          console.log("[ERROR] Couldn't get access token in getImage().");
        });
    },
    setImageSource(guid) {
      this.$nextTick(() => {
        let imgRef = this.$refs['thumbnail-' + guid];

        if (!imgRef) {
          return;
        }

        let imageSrc = 'data:image/jpeg;base64,' + this.thumbnails[guid];

        imgRef.src = imageSrc;

        this.imageLoaderKey += 1;
      });
    },
    async onModelUploaded() {
      await this.loadModels();
    },
  },
  created() {
    this.$AuthService.getAccessToken(
      async () => {
        if (this.$route.query["add"]) {
          routerUtils.removeQueryParam(this, "add");
          this.$bvModal.show(this.MODAL_ID_UPLOAD);
        }

        await this.loadModels();
      },
      () => {
        // eslint-disable-next-line no-console
        console.log("[ERROR] Couldn't get access token in created().");
      }
    );
  },
  mounted() {
    this.$root.$on('bv::modal::hidden', (bvEvent, modalId) => {
      if (modalId === this.MODAL_ID_VIEW) {
        this.showViewerComponent = false;
      }
    });
  },
}
</script>
